import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Card,
  Button,
  Badge,
  Form,
} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import { hasPermission, PERMISSIONS } from "utils/permissions";
import paginationFactory from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import { useNavigate } from "react-router-dom";
import HusariaHeader from "../components/HusariaHeader";

const RegistrationsOverview = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);
  const canEdit = hasPermission(userData, PERMISSIONS.EDIT);
  const canPerformActions =
    hasPermission(userData, PERMISSIONS.CREATE) ||
    hasPermission(userData, PERMISSIONS.EDIT) ||
    hasPermission(userData, PERMISSIONS.DELETE);
  const [registrations, setRegistrations] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const visibleColumns = [
    { key: "user", label: "User" },
    { key: "agent", label: "Agent" },
    { key: "contact", label: "Contact" },
    { key: "lan_ip", label: "LAN IP" },
    { key: "ip", label: "IP" },
    { key: "port", label: "Port" },
    { key: "hostname", label: "Hostname" },
    { key: "status", label: "Status" },
    { key: "ping", label: "Ping" },
    { key: "profile", label: "Profile" },
    { key: "actions", label: "Actions" },
  ];
  const rowKey = "user";
  const fetchRegistrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${serverHost}/registrations/all`,
      );
      if (!response.ok) throw new Error("Failed to fetch registrations");
      const data = await response.json();
      console.log(data.result);
      setRegistrations(data.result || []);

      console.log("Refreshing registrations...");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnregister = async (user) => {
    try {
      // const response = await fetch(`${serverHost}/unregister`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ user }),
      // });
      // if (!response.ok) throw new Error("Failed to unregister");

      console.log(`Unregister user: ${user}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleProvision = async (user) => {
    try {
      // const response = await fetch(`${serverHost}/provision`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ user }),
      // });
      // if (!response.ok) throw new Error("Failed to provision");

      console.log(`Provision user: ${user}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReboot = async (user) => {
    try {
      // const response = await fetch(`${serverHost}/reboot`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ user }),
      // });
      // if (!response.ok) throw new Error("Failed to reboot");

      console.log(`Reboot user: ${user}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = visibleColumns.map((col, index) => ({
    dataField: col.key,
    text: col.label,
    sort: true,
    formatter: (cell, row) => {
      if (index === 0) {
        return canEdit ? (
          <a
            style={{
              color: isDark ? "#60a5fa" : "#0d6efd",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "none",
            }}
          >
            {cell ?? "-"}
          </a>
        ) : (
          <span style={{ fontWeight: 600 }}>{cell ?? "-"}</span>
        );
      }

      return cell ?? "-";
    },
  }));

  if (canPerformActions) {
    columns.push({
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => (
        <div className="d-flex gap-1 justify-content-center flex-wrap">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleUnregister(row.user)}
          >
            Unregister
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleProvision(row.user)}
          >
            Provision
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => handleReboot(row.user)}
          >
            Reboot
          </Button>
        </div>
      ),
    });
  }

  const filteredRows = React.useMemo(() => {
    if (!searchTerm) return registrations;

    return registrations.filter((row) =>
      // AND matches search term
      visibleColumns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    );
  }, [registrations, searchTerm, visibleColumns]);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // "asc" | "desc"
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "registered")
      return <Badge bg="success">Registered</Badge>;
    if (statusLower === "expired")
      return (
        <Badge bg="warning" text="dark">
          Expired
        </Badge>
      );
    if (statusLower === "unregistered")
      return <Badge bg="danger">Unregistered</Badge>;
    return (
      <Badge bg="secondary" style={{ color: "black" }}>
        {status || "Unknown"}
      </Badge>
    );
  };

  const handleDomainClick = async (domain) => {
      //set the domain
      try {
        console.log(domain);
        const response = await fetch(`${serverHost}/fetch_site_info/${domain}`, {
          headers: {
            Accept: "application/json",
          },
        });
        if (!response.ok) throw new Error("Failed to site info.");
        const data = await response.json();
        localStorage.setItem("selectedSite", JSON.stringify(data.site));
        localStorage.setItem("clientSites", JSON.stringify(data.client_sites));
        localStorage.setItem("clientId", JSON.stringify(data.client_uuid));
        window.dispatchEvent(
          new CustomEvent("siteChanged", {
            detail: data.site,
          }),
        );
        navigate("/admin/hucs/registrations");
      } catch (err) {
        console.log("error: failed to get domain ", err);
      }
    };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      handleDomainClick(row.domain)
    }
  }

  return (
    <>
      <HusariaHeader name="Registrations" parentName="Dealer" padding="4.5rem"/>

      <Container fluid className="mt--6">
        {/* Registrations Table */}
        <div style={{marginBottom: "8px"}}>
                <span className="return-dealer-menu" onClick={() => navigate('/admin/dealer-menu')}>
                  <i class="fas fa-caret-left"></i> Return to Dealer Menu
                </span>
                </div>
                <Card

          className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
        >
          <Card.Header
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
              color: isDark ? "#e2e8f0" : "#111",
            }}
          >
            <div className="d-flex align-items-center">
              <h4 className="mb-2 mb-md-0">
                <Badge
                  bg="primary"
                  className="ms-2"
                  style={{ marginLeft: "0.5rem", marginRight: "1rem" }}
                >
                  {filteredRows.length} / {registrations.length} Regisrtations
                </Badge>
              </h4>
              <Form className="navbar-search navbar-search-light form-inline search-border">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative input-group-merge">
                    <InputGroupText>
                      <i className="fas fa-search search-icon" />
                    </InputGroupText>
                    <Input
                      className="table-search-bar form-control "
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>
            <div className="d-flex gap-10 flex-wrap align-items-center">
              <Button
                variant="info"
                size="sm"
                onClick={fetchRegistrations}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            <div>
              <div className="py-4 table-responsive">
                <BootstrapTable
                  keyField={rowKey}
                  data={filteredRows} // <-- use filteredRows instead of all rows
                  columns={columns}
                  bootstrap4
                  pagination={pagination}
                  bordered={false}
                  hover
                  rowEvents={rowEvents}
                  rowStyle={{ cursor: "pointer" }}
                  classes="align-items-center table-flush"
                  noDataIndication={() =>
                    loading ? (
                      <div className="text-center">Loading...</div>
                    ) : (
                      <div className="text-center">No records found.</div>
                    )
                  }
                />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default RegistrationsOverview;
